/**
 * Track Changes Helper (F-13)
 *
 * Enables applying AI edits as Word Tracked Changes so they appear
 * in Word's native Review pane with first-class review status.
 *
 * Uses `document.changeTrackingMode` (WordApi 1.4+).
 * Gracefully falls back if the API is not available.
 */

/**
 * Check if Track Changes API is available.
 */
export const isTrackChangesSupported = (): boolean => {
    try {
        // changeTrackingMode was introduced in WordApi 1.4
        return typeof Word !== 'undefined' && typeof Word.ChangeTrackingMode !== 'undefined';
    } catch {
        return false;
    }
};

/**
 * Apply content as a tracked change.
 *
 * Sets the document to "Track All" mode, performs the insertion,
 * then restores the original tracking mode.
 *
 * @param context - Word request context
 * @param range - Target range to replace
 * @param newContent - New text or HTML content
 * @param mode - 'text' for plain text, 'html' for HTML insertion
 * @returns true if successful, false if fallback is needed
 */
export const applyWithTrackedChanges = async (
    context: Word.RequestContext,
    range: Word.Range,
    newContent: string,
    mode: 'text' | 'html' = 'text',
    fontName?: string | null,
): Promise<boolean> => {
    // Declared outside the try so the catch can put the user's own setting back.
    let previousMode: Word.Document['changeTrackingMode'] | undefined;

    try {
        if (!isTrackChangesSupported()) {
            console.warn('[trackChangesHelper] Track Changes API not available');
            return false;
        }

        // Save the current tracking mode
        const doc = context.document;
        doc.load('changeTrackingMode');
        await context.sync();

        previousMode = doc.changeTrackingMode;

        // Enable track changes
        doc.changeTrackingMode = Word.ChangeTrackingMode.trackAll;
        await context.sync();

        // Perform the insertion — Word will record it as a tracked change
        const insertedRange =
            mode === 'html'
                ? range.insertHtml(newContent, Word.InsertLocation.replace)
                : range.insertText(newContent, Word.InsertLocation.replace);
        await context.sync();

        if (fontName && fontName.trim() && fontName !== 'Mixed') {
            insertedRange.font.name = fontName;
            range.font.name = fontName;
        }
        await context.sync();

        // Restore the previous tracking mode
        doc.changeTrackingMode = previousMode;
        await context.sync();

        return true;
    } catch (err) {
        console.warn('[trackChangesHelper] applyWithTrackedChanges failed:', err);
        // Restore what the user had, never force 'off' — that would silently stop
        // tracking for someone who had Track Changes on document-wide.
        try {
            if (previousMode !== undefined) {
                context.document.changeTrackingMode = previousMode;
                await context.sync();
            }
        } catch {
            // Ignore cleanup errors
        }
        return false;
    }
};
